import { useCallback, useEffect, useRef, useState } from "react";
import WriteDivField from "../Components/WriteDivField";
import { TabView, TabPanel } from "primereact/tabview";
import {
  apisHeaders,
  normalizeNumber,
  removeSpecialCharAndNumbers,
} from "../utils";
import { DeleteSweetAlert } from "../DeleteSweetAlert";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { addInvoice } from "../apis/invoiceSlice";
import { useNavigate } from "react-router-dom";
import _ from "lodash";
import {
  getMatchingCreditEntitys,
  setCreditEntitys,
} from "src/apis/entitySlice";
import "../style.scss";
import axios from "axios";

let rowIdCounter = 0;
const getUniqueRowId = () => ++rowIdCounter;

const getInitialRow = () => ({
  id: getUniqueRowId(),
  item: "",
  pcs: "",
  qty: "",
  rate: "",
  amount: "",
});

const getInitialRows = () => [
  getInitialRow(),
  getInitialRow(),
  getInitialRow(),
];

export default function WholeSaleAddInvoice() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { loggedIn } = useSelector((state) => state.authData);
  const { creditEntitys, entLoading, entityAns } = useSelector(
    (state) => state.entData
  );
  // Tabs state
  const [tabs, setTabs] = useState([
    {
      name: "Tab 1",
      billNo: "",
      invoice_type: "K",
      entityName: "",
      entityId: "",
      writing: false,
      rows: getInitialRows(),
    },
    {
      name: "Tab 2",
      billNo: "",
      invoice_type: "C",
      entityName: "",
      entityId: "",
      writing: false,
      rows: getInitialRows(),
    },
    {
      name: "Tab 3",
      billNo: "",
      invoice_type: "U",
      entityName: "",
      entityId: "",
      writing: false,
      rows: getInitialRows(),
    },
  ]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [btnLoading, setBtnLoading] = useState(false);
  const [itemLoadingRow, setItemLoadingRow] = useState(null);
  const handleCustInput = (value) => {
    sellDebouncedSearch(value);
  };

  const sellDebouncedSearch = useCallback(
    _.debounce(async (searchName) => {
      if (searchName) {
        const response = await dispatch(
          getMatchingCreditEntitys({
            uId: loggedIn?._id,
            type: "C",
            entName: searchName,
          })
        );
        // console.log("Credit response", response);
        if (response.payload?.error) {
          toast.error(response.payload.message);
        }
      }
    }, 2000),
    [loggedIn]
  );

  // Refs and gesture state
  const pcsTimeoutRef = useRef(null);

  // Refs for WriteDivFields: rows x fields
  const rowRefs = useRef({});

  // Get current tab's and rows
  const rows = tabs[activeIndex]?.rows || getInitialRows();

  // Helper for numerals only
  const numeralsOnly = (input) => normalizeNumber(input).replace(/[^0-9]/g, "");

  // Update rows for current tab
  const setRowsForTab = (newRows) => {
    setTabs((prevTabs) => {
      const updated = [...prevTabs];
      updated[activeIndex] = {
        ...updated[activeIndex],
        rows: newRows,
      };
      return updated;
    });
  };

  // Delete row by index and clear WriteDivField DOM content
  const deleteRow = (rowIndex) => {
    const rowId = rows[rowIndex].id;
    if (rowRefs.current[rowId]) {
      rowRefs.current[rowId].forEach((ref) => {
        if (ref && ref.innerText !== undefined) ref.innerText = "";
      });
      delete rowRefs.current[rowId];
    }
    const newRows = rows.filter((_, i) => i !== rowIndex);
    setRowsForTab(newRows);
  };

  const handeRemoveRowConfirm = async (data, index) => {
    if (data && index !== undefined && index !== null) {
      const response = () => deleteRow(index);
      DeleteSweetAlert({
        title: "Are you sure you want to remove this entry?",
        icon1: "warning",
        title2: `${data?.item}`,
        callApi: response,
        callSuccessModal: false,
      });
    }
  };

  // Debounced API call for item search
  const debouncedItemSearch = useCallback(
    _.debounce(async (value, rowIndex, onDone) => {
      if (!value) {
        if (onDone) onDone();
        return;
      }
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_API}/item/findItem`,
          { uId: loggedIn?._id, item_name: value },
          apisHeaders
        );
        const data = response?.data?.data;
        if (data && data?.item_name && data?.item_rate) {
          const updatedRows = [...rows];
          updatedRows[rowIndex] = {
            ...updatedRows[rowIndex],
            item: data.item_name,
            rate: data.item_rate.toString(),
          };
          // Optionally, recalculate amount if qty is present
          const qty = parseInt(updatedRows[rowIndex].qty || "0", 10);
          if (!isNaN(qty)) {
            updatedRows[rowIndex].amount = (
              qty * parseInt(data.item_rate, 10)
            ).toString();
          }
          if (
            rowIndex === updatedRows.length - 2 ||
            rowIndex === updatedRows.length - 1
          ) {
            for (let i = 0; i < 5; i++) {
              updatedRows.push(getInitialRow());
            }
          }
          setRowsForTab(updatedRows);
        }
        // If not found, do nothing
      } catch (err) {
        // Optionally handle error
        console.log("err", err);
      }
      if (onDone) onDone();
    }, 1000),
    [loggedIn, rows] // <-- add rows as dependency
  );
  // Handle input for any field in a row
  const handleInput = (rowIndex, field, value) => {
    const newRows = [...rows];
    if (field === "qty" || field === "amount") {
      const normalized = normalizeNumber(value);
      newRows[rowIndex][field] = normalized.replace(/[^0-9]/g, "");
    } else if (field === "pcs") {
      newRows[rowIndex][field] = value;
      if (pcsTimeoutRef.current) clearTimeout(pcsTimeoutRef.current);
      pcsTimeoutRef.current = setTimeout(() => {
        updatePcsBelow(rowIndex, value);
      }, 500);
    } else if (field === "item") {
      newRows[rowIndex][field] = value;
      setItemLoadingRow(rowIndex); // Set loading row
      debouncedItemSearch(value, rowIndex, () => setItemLoadingRow(null)); // Pass callback to clear loading
    } else {
      newRows[rowIndex][field] = value;
    }

    if (field === "qty" || field === "rate") {
      const qty = parseInt(
        field === "qty"
          ? normalizeNumber(value).replace(/[^0-9]/g, "")
          : newRows[rowIndex].qty || "0",
        10
      );
      const rate = parseInt(
        field === "rate"
          ? normalizeNumber(value).replace(/[^0-9]/g, "")
          : newRows[rowIndex].rate || "0",
        10
      );
      if (!isNaN(qty) && !isNaN(rate)) {
        newRows[rowIndex].amount = (qty * rate).toString();
      } else {
        newRows[rowIndex].amount = "";
      }
    }

    if (field !== "item") {
      if (rowIndex === newRows.length - 2 || rowIndex === newRows.length - 1) {
        for (let i = 0; i < 5; i++) {
          newRows.push(getInitialRow());
        }
      }
    }

    setRowsForTab(newRows);
  };

  // Update pcs for all rows below the given index
  const updatePcsBelow = (rowIndex, value) => {
    const newRows = [...rows];
    for (let i = rowIndex + 1; i < newRows.length; i++) {
      newRows[i].pcs = value;
    }
    setRowsForTab(newRows);
  };

  // Add new tab (max 10)
  const addTab = () => {
    if (tabs.length >= 10) return;
    setTabs((prevTabs) => [
      ...prevTabs,
      {
        name: `Tab ${prevTabs.length + 1}`,
        billNo: "",
        invoice_type: "K",
        entityName: "",
        entityId: "",
        writing: false,
        rows: getInitialRows(),
      },
    ]);
    setActiveIndex(tabs.length);
    rowRefs.current = [];
  };

  const removeTab = (index) => {
    setTabs((prevTabs) => {
      const updated = prevTabs.filter((_, i) => i !== index);
      return updated.length === 0
        ? [
            {
              name: "Tab 1",
              billNo: "",
              invoice_type: "K",
              entityName: "",
              entityId: "",
              writing: false,
              rows: getInitialRows(),
            },
          ]
        : updated;
    });
    setActiveIndex((prev) => (prev > 0 ? prev - 1 : 0));
  };

  const handleInvoiceTypeChange = (i, value) => {
    setTabs((prevTabs) => {
      const updated = [...prevTabs];
      updated[i] = { ...updated[i], invoice_type: value };
      return updated;
    });
  };

  const handleToggleWriting = (boolean) => {
    setTabs((prevTabs) => {
      const updated = [...prevTabs];
      updated[activeIndex] = {
        ...updated[activeIndex],
        writing: boolean,
      };
      return updated;
    });
  };

  useEffect(() => {
    if (creditEntitys.length > 0 && entityAns) {
      setTabs((prevTabs) => {
        const updated = [...prevTabs];
        updated[activeIndex] = {
          ...updated[activeIndex],
          entityId: creditEntitys[0]?._id,
          entityName: creditEntitys[0]?.searchName,
        };
        return updated;
      });
      setTimeout(() => {
        handleToggleWriting(false);
      }, 1000);
    }
  }, [creditEntitys]);

  const handleSubmit = async () => {
    if (rows.filter((r) => r.item && r.item.trim() !== "").length === 0) {
      toast.warning("Please add at least one item.");
      return;
    }
    setBtnLoading(true);
    const finalData = {
      uId: loggedIn?._id,
      entityName: tabs[activeIndex]?.entityName,
      entityId: tabs[activeIndex]?.entityId,
      invoice_type: tabs[activeIndex]?.invoice_type,
      items: rows.filter((r) => r.item && r.item.trim() !== ""),
      qty: rows
        .map((r) => parseInt(r.qty || "0", 10))
        .reduce((a, b) => a + b, 0),
      totalAmt: rows
        .map((r) => parseInt(r.amount || "0", 10))
        .reduce((a, b) => a + b, 0),
    };
    const response = await dispatch(addInvoice(finalData));
    if (!response?.payload?.error) {
      toast.success(response?.payload?.message);
      setBtnLoading(false);
      removeTab(activeIndex); // Remove the active tab after success
      dispatch(setCreditEntitys([]));
      return;
    } else {
      toast.error(response?.payload?.message);
    }
    setBtnLoading(false);
  };

  return (
    <div className="m-[0.3125rem_0.375rem]">
      <TabView
        activeIndex={activeIndex}
        onTabChange={(e) => {
          setActiveIndex(e.index);
          dispatch(setCreditEntitys([]));
        }}
        renderActiveOnly={true}
      >
        {tabs?.map((tab, i) => (
          <TabPanel
            key={i}
            header={
              <span
                className={`flex items-center gap-2 ${
                  i === activeIndex && i !== tabs?.length - 1
                    ? "py-1.5"
                    : i === tabs?.length - 1
                    ? "relative py-1"
                    : "py-1.5"
                }`}
              >
                {tab?.name}
                {/* {tabs?.length > 1 && (
                  <i
                    className="pi pi-times text-xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeTab(i);
                    }}
                  ></i>
                )} */}
                {i === tabs?.length - 1 && (
                  <div
                    className="p-1 flex items-center justify-center rounded-sm"
                    style={{
                      backgroundColor:
                        tabs?.length === 10 ? "#c2baba" : "#6366f1",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      addTab();
                    }}
                  >
                    <i
                      className={`pi pi-plus text-xs ${
                        tabs?.length === 10 ? "text-gray-400" : "text-white"
                      } `}
                    ></i>
                  </div>
                )}
              </span>
            }
          >
            <>
              <div className="flex items-center gap-2 mt-2">
                <Button
                  icon={"pi pi-home"}
                  className="h-[3rem]"
                  onClick={() => navigate("/")}
                />
                <div className="w-[41%] relative">
                  <WriteDivField
                    onClick={() => handleToggleWriting(true)}
                    onClear={() =>
                      setTabs((prevTabs) => {
                        const updated = [...prevTabs];
                        updated[activeIndex] = {
                          ...updated[activeIndex],
                          entityId: "",
                          entityName: "",
                        };
                        return updated;
                      })
                    }
                    style={{ border: "1px solid black" }}
                    parentClassName={`w-full h-[3rem] outline-none border border-black p-0.5`}
                    className="w-full h-full outline-none text-[1.5rem] flex items-center"
                    handleInput={(val) => {
                      setTabs((prevTabs) => {
                        const updated = [...prevTabs];
                        updated[activeIndex] = {
                          ...updated[activeIndex],
                          entityName: val,
                        };
                        return updated;
                      });
                      handleCustInput(val);
                    }}
                    placeholder="Customer"
                    value={tabs[activeIndex]?.entityName || ""}
                  />
                  {tabs[activeIndex]?.writing && (
                    <div className={"selectBox"}>
                      {entLoading ? (
                        <div
                          style={{ width: "100%", height: "12rem" }}
                          className="flex items-center justify-center"
                        >
                          <i
                            className="pi pi-spin pi-spinner text-blue-500"
                            style={{ fontSize: "3rem" }}
                          ></i>
                        </div>
                      ) : (
                        <>
                          <div
                            className="w-full py-1 px-2 bg-red-500 rounded-md flex items-center justify-center"
                            onClick={() => handleToggleWriting(false)}
                          >
                            <i className="pi pi-times text-white"></i>
                          </div>
                          {creditEntitys.length > 0 ? (
                            <>
                              {creditEntitys.map((item, index) => (
                                <option
                                  key={index}
                                  value={item._id}
                                  className={`w-full py-1 px-2 rounded-md flex items-center ${
                                    tab.entityId === item._id
                                      ? "bg-blue-500 text-white"
                                      : "bg-gray-200"
                                  }`}
                                  onClick={() => {
                                    setTabs((prevTabs) => {
                                      const updated = [...prevTabs];
                                      updated[activeIndex] = {
                                        ...updated[activeIndex],
                                        entityId: item._id,
                                        entityName: item.searchName,
                                      };
                                      return updated;
                                    });
                                    handleToggleWriting(false);
                                  }}
                                >
                                  {item.searchName}
                                </option>
                              ))}
                            </>
                          ) : (
                            <div
                              style={{
                                width: "100%",
                                height: "12rem",
                                fontWeight: "700",
                              }}
                              className="flex items-center justify-center"
                            >
                              No Data Found
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex gap-6 items-center">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      inputId={`cash-${i}`}
                      checked={tab.invoice_type === "K"}
                      onChange={() => handleInvoiceTypeChange(i, "K")}
                    />
                    <label htmlFor={`cash-${i}`}>Cash</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      inputId={`credit-${i}`}
                      checked={tab.invoice_type === "C"}
                      onChange={() => handleInvoiceTypeChange(i, "C")}
                    />
                    <label htmlFor={`credit-${i}`}>Credit</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      inputId={`uchak-${i}`}
                      checked={tab.invoice_type === "U"}
                      onChange={() => handleInvoiceTypeChange(i, "U")}
                    />
                    <label htmlFor={`uchak-${i}`}>Uchak</label>
                  </div>
                </div>

                {/* <WriteDivField
                  style={{ border: "1px solid black" }}
                  parentClassName={`w-[41%] h-[3rem] outline-none border border-black p-0.5`}
                  className="w-full h-full outline-none text-[1.5rem] flex items-center"
                  handleInput={(val) => {
                    setTabs((prevTabs) => {
                      const updated = [...prevTabs];
                      updated[activeIndex] = {
                        ...updated[activeIndex],
                        billNo: val,
                      };
                      return updated;
                    });
                  }}
                  placeholder="Bill No"
                  value={tabs[activeIndex]?.billNo || ""}
                /> */}
              </div>
              <div className="mt-2 max-h-[92vh] overflow-y-scroll">
                {rows.map((row, index) => {
                  const rowId = row.id;
                  if (!rowRefs.current[rowId]) rowRefs.current[rowId] = [];
                  return (
                    <div
                      key={rowId}
                      className="flex select-none"
                      tabIndex={0}
                      onDoubleClick={() => handeRemoveRowConfirm(row, index)}
                    >
                      <div
                        className={`w-[8%] h-[3rem] p-0.5`}
                        style={{ border: "1px solid black" }}
                      >
                        <div className="w-full h-full outline-none text-[1.5rem] flex items-center ps-1">
                          {index + 1}
                        </div>
                      </div>
                      <WriteDivField
                        ref={(el) => {
                          if (!rowRefs.current[rowId])
                            rowRefs.current[rowId] = [];
                          if (el) {
                            rowRefs.current[rowId][0] = el;
                          } else {
                            rowRefs.current[rowId][0] = undefined;
                          }
                        }}
                        style={{ border: "1px solid black" }}
                        parentClassName={`w-[37%] h-[3rem] outline-none border border-black p-0.5`}
                        className="w-full h-full outline-none text-[1.5rem] flex items-center"
                        handleInput={(val) => handleInput(index, "item", val)}
                        placeholder="Item"
                        value={row.item}
                      />
                      <WriteDivField
                        ref={(el) => {
                          if (!rowRefs.current[rowId])
                            rowRefs.current[rowId] = [];
                          if (el) {
                            rowRefs.current[rowId][1] = el;
                          } else {
                            rowRefs.current[rowId][1] = undefined;
                          }
                        }}
                        style={{ border: "1px solid black" }}
                        parentClassName={`w-[15%] h-[3rem] outline-none border border-black p-0.5`}
                        className="w-full h-full outline-none text-[1.5rem] flex items-center"
                        handleInput={(val) => handleInput(index, "pcs", val)}
                        placeholder="Pices"
                        value={row.pcs}
                      />
                      <WriteDivField
                        ref={(el) => {
                          if (!rowRefs.current[rowId])
                            rowRefs.current[rowId] = [];
                          if (el) {
                            rowRefs.current[rowId][2] = el;
                          } else {
                            rowRefs.current[rowId][2] = undefined;
                          }
                        }}
                        style={{ border: "1px solid black" }}
                        parentClassName={`w-[15%] h-[3rem] outline-none border border-black p-0.5`}
                        className="w-11/12 h-full outline-none text-[1.5rem] flex items-center"
                        handleInput={(val) => handleInput(index, "qty", val)}
                        placeholder="Qty"
                        value={row.qty}
                        sanitize={numeralsOnly}
                      />
                      {itemLoadingRow === index ? (
                        <div
                          style={{ border: "1px solid black" }}
                          className="w-[15%] h-[3rem] outline-none flex items-center justify-start p-0.5"
                        >
                          <i className="pi pi-spin pi-spinner text-blue-500 text-lg"></i>
                        </div>
                      ) : (
                        <WriteDivField
                          ref={(el) => {
                            if (!rowRefs.current[rowId])
                              rowRefs.current[rowId] = [];
                            if (el) {
                              rowRefs.current[rowId][3] = el;
                            } else {
                              rowRefs.current[rowId][3] = undefined;
                            }
                          }}
                          style={{ border: "1px solid black" }}
                          parentClassName={`w-[15%] h-[3rem] outline-none border border-black p-0.5`}
                          className="w-11/12 h-full outline-none text-[1.5rem] flex items-center"
                          handleInput={(val) => handleInput(index, "rate", val)}
                          placeholder="Rate"
                          value={row.rate}
                          sanitize={numeralsOnly}
                        />
                      )}
                      <WriteDivField
                        ref={(el) => {
                          if (!rowRefs.current[rowId])
                            rowRefs.current[rowId] = [];
                          if (el) {
                            rowRefs.current[rowId][4] = el;
                          } else {
                            rowRefs.current[rowId][4] = undefined;
                          }
                        }}
                        style={{ border: "1px solid black" }}
                        parentClassName={`w-[20%] h-[3rem] outline-none border border-black p-0.5`}
                        className="w-11/12 h-full outline-none text-[1.5rem] flex items-center"
                        handleInput={(val) => handleInput(index, "amount", val)}
                        placeholder="Amount"
                        value={row.amount}
                        sanitize={numeralsOnly}
                      />
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between mt-2">
                <div className="text-xl font-bold">
                  {`Total Item(S):${
                    rows.filter((r) => r.item && r.item.trim() !== "").length
                  }`}
                  &nbsp;/
                  {`Qty: ${rows
                    .map((r) => parseInt(r.qty || "0", 10))
                    .reduce((a, b) => a + b, 0)}`}
                </div>
                <div className="text-xl font-bold">
                  {`Total: ${rows
                    .map((r) => parseInt(r.amount || "0", 10))
                    .reduce((a, b) => a + b, 0)}`}
                </div>
              </div>
              <div className="mt-2 flex justify-center items-center">
                <Button
                  label="Submit"
                  className="w-[90%] py-1.5"
                  onClick={handleSubmit}
                  loading={btnLoading}
                />
              </div>
            </>
          </TabPanel>
        ))}
      </TabView>
    </div>
  );
}
